import { DeviceARPDataType } from '../types/buildingTypes';
import { VendorType } from '../types/types';

/**
 * Maximum number of ARP rows that can be selected for a single bulk DHCP
 * reservation request.
 */
export const BULK_DHCP_RESERVATION_SELECTION_CAP = 10;

/**
 * Bulk DHCP reservation creation is restricted to Juniper Core/Distro
 * devices, and only for rows that don't already have a reservation.
 */
export const isEligibleForBulkDhcpReservation = (
  vendor: string,
  row: Pick<DeviceARPDataType, 'dhcp_reservation'>
): boolean => vendor === VendorType.juniper && row.dhcp_reservation.toLowerCase() === 'no';

/**
 * Human-readable explanation for why a row's checkbox is disabled, shown in
 * a tooltip next to the checkbox.
 */
export const getBulkDhcpReservationIneligibilityReason = (
  vendor: string,
  row: Pick<DeviceARPDataType, 'dhcp_reservation'>
): string => {
  if (vendor !== VendorType.juniper) {
    return 'Bulk DHCP reservation is only available for Juniper devices.';
  }
  if (row.dhcp_reservation.toLowerCase() !== 'no') {
    return 'This row already has a DHCP reservation and cannot be bulk-reserved.';
  }
  return '';
};

/**
 * Finds IP addresses that appear more than once among the given rows.
 */
export const findDuplicateReservationIps = (
  rows: Pick<DeviceARPDataType, 'ip_address'>[]
): string[] => {
  const counts = new Map<string, number>();
  rows.forEach(({ ip_address }) => {
    counts.set(ip_address, (counts.get(ip_address) ?? 0) + 1);
  });

  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([ip_address]) => ip_address);
};

/**
 * Finds new-description values (case-insensitive) that are reused across
 * more than one row in the same batch. Empty descriptions are ignored here
 * since they're already caught by per-row required-field validation.
 */
export const findDuplicateDescriptions = (entries: { description: string }[]): string[] => {
  const counts = new Map<string, number>();

  entries.forEach(({ description }) => {
    const normalized = description.trim().toLowerCase();
    if (!normalized) return;
    counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
  });

  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([description]) => description);
};
